'use client';

import { Tabs, TabsContent, TabsList, TabsTrigger, type tabsListVariants } from '@zealot/ui/components/tabs';
import { cn } from '@zealot/ui/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import type * as React from 'react';

type SimpleTabItem = {
  value: string;
  label: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
  icon?: React.ReactNode;
};

type SimpleTabsProps = {
  items: SimpleTabItem[];
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  variant?: VariantProps<typeof tabsListVariants>['variant'];
  size?: VariantProps<typeof tabsListVariants>['size'];
  className?: string;
  listClassName?: string;
  triggerClassName?: string;
  contentClassName?: string;
};

const SimpleTabs = ({
  items,
  defaultValue,
  value,
  onValueChange,
  variant,
  size,
  className,
  listClassName,
  triggerClassName,
  contentClassName,
}: SimpleTabsProps) => {
  const resolvedDefaultValue = defaultValue ?? items[0]?.value;

  return (
    <Tabs
      data-slot="simple-tabs"
      defaultValue={resolvedDefaultValue}
      value={value}
      onValueChange={onValueChange}
      className={className}
    >
      <TabsList variant={variant} size={size} className={listClassName}>
        {items.map((item) => (
          <TabsTrigger
            key={item.value}
            value={item.value}
            disabled={item.disabled}
            size={size}
            className={cn(item.icon && 'gap-1.5', triggerClassName)}
          >
            {item.icon}
            {item.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {items.map((item) => (
        <TabsContent key={item.value} value={item.value} className={contentClassName}>
          {item.content}
        </TabsContent>
      ))}
    </Tabs>
  );
};

export type { SimpleTabItem, SimpleTabsProps };
export { SimpleTabs };
